import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Eventcalendar,
  locale,
  MbscCalendarEvent,
  MbscCalendarEventData,
  MbscEventcalendarView,
  MbscEventClickEvent,
  MbscPageLoadingEvent
} from "@mobiscroll/react";
import { MRT_TableInstance } from "material-react-table";
import {
  Box,
  Grid2,
  DialogTitle,
  Dialog,
  DialogContent,
  IconButton,
  useTheme
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { RootState } from "../../../app/rootReducer";
import { useSelector } from "../../../app/helpers";
import { FleetManagementRow } from "../types/FleetManagement-row.types";
import { VehicleBlockingEvent } from "../types/FleetManagement.types";
import { OccupiedStatus } from "../../FacilityManager/Domain/Types/Status";
import { FleetManagementEventDetailTable } from "./FleetManagementEventDetailTable.component";
import { useRemoteFetchUserInfoForAllUsers } from "../../../hooks/Remote/User/UserInfo/useRemoteFetchUserInfoForAllUsers";
import { useStableFleetData } from "./hooks/useStableFleetData.hook";
import { useRemoteFetchBlockingEvents } from "../../../hooks/Remote/Fleet/useRemoteFetchBlockingEvents";
import { useRemoteFetchBlockingEventsCalendar } from "../../../hooks/Remote/Fleet/useRemoteFetchBlockingEventsCalendar";
import { useRemoteFetchVehicleBookingEvents } from "../../../hooks/Remote/Fleet/useRemoteFetchVehicleBookingEvents";
import { useRemoteFetchBookingEventsCalendar } from "../../../hooks/Remote/Fleet/useRemoteFetchBookingEventsCalendar";
import { useRemoteCreateBlockingEvent } from "../../../hooks/Remote/Fleet/useRemoteCreateBlockingEvent";
import { useRemoteUpdateBlockingEvent } from "../../../hooks/Remote/Fleet/useRemoteUpdateBlockingEvent";
import { useRemoteDeleteBlockingEvent } from "../../../hooks/Remote/Fleet/useRemoteDeleteBlockingEvent";
import { useRemoteCancelFleetBooking } from "../../../hooks/Remote/Fleet/useRemoteCancelFleetBooking";
import { useRemoteCheckOutFleetBooking } from "../../../hooks/Remote/Fleet/useRemoteCheckOutFleetBooking";
import { useRemoteCheckInFleetBooking } from "../../../hooks/Remote/Fleet/useRemoteCheckInFleetBooking";
import {
  BookOnBehalfFleetPayload,
  useRemoteBookOnBehalfFleetBooking
} from "../../../hooks/Remote/Fleet/useRemoteBookOnBehalfFleetBooking";
import { useRemoteUpdateFleetBookingTimes } from "../../../hooks/Remote/Fleet/useRemoteUpdateFleetBookingTimes";
import { BookingType } from "../../../features/Booking-Form/typings/booking-inputs";
import { CheckInDialog } from "./CheckInDialog.component";
import { generateTodayColor } from "../../Schedule/ScheduleCalendar/functions/generateScheduleColor.functions";
import { getDefaultDateRange } from "../FleetManagementTable.component";
import { enrichBlockingEventsWithCreatedByUser } from "./functions/enrichBlockingEventsWithCreatedByUser.function";
import { handleFleetRequestError } from "./functions/handleFleetRequestError.function";
import { useFleetCalendarParams, type VisibleCalendarRange } from "./hooks/useFleetCalendarParams";
import { useFleetEventsState } from "./hooks/useFleetEventsState";
import {
  FleetEventTooltipContents,
  FleetEventTooltipContentsUnion
} from "./partial/FleetEventTooltipContents.partial";

export type FleetCalendarEvent = MbscCalendarEvent & {
  originalData?: OccupiedStatus | VehicleBlockingEvent;
};

const calendarMonthView: MbscEventcalendarView = {
  calendar: {
    type: "month"
  }
};

type Props = {
  open: boolean;
  isEditable: boolean;
  table: MRT_TableInstance<FleetManagementRow>;
  selectedRow: FleetManagementRow | null;
  onClose: () => void;
  /** When true, shows events from all fleet items instead of just selectedRow */
  showAllEvents?: boolean;
  /** All fleet data to show when showAllEvents is true */
  allFleetData?: FleetManagementRow[];
  /** Locations available for booking (used for location dropdown when creating a booking). */
  locationOptions?: Array<{ id: number; name: string }>;
  /** Called after creating a vehicle booking so the parent can refetch fleet data and show the new booking in the schedule. */
  onFleetDataRefetch?: () => void;
  /** Date range derived from booking inputs; used in booking mode to scope event fetches to the selected dates. */
  bookingDateRange?: { start: DateTime; end: DateTime };
};

export function FleetManagementEventDetailDialog({
  open,
  isEditable,
  selectedRow,
  onClose,
  showAllEvents = false,
  allFleetData = [],
  locationOptions = [],
  onFleetDataRefetch,
  bookingDateRange
}: Readonly<Props>) {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const {
    settings: { timezone }
  } = useSelector((state: RootState) => state.login);
  const { enqueueSnackbar } = useSnackbar();

  const calendarColors = useMemo(() => generateTodayColor({ theme }), [theme.palette.mode]);

  const { data: remoteUsers } = useRemoteFetchUserInfoForAllUsers();

  const stableAllFleetData = useStableFleetData(allFleetData);

  // Admin (isEditable): no date range for fetches — single-vehicle gets all events; all-events calendar uses visible month range (refetch on month change).
  // Booking mode: use bookingDateRange (from booking inputs) when available, otherwise fall back to default range.
  const bookingRange = bookingDateRange ?? getDefaultDateRange();
  const wideRangeForAdmin = useMemo(
    () => ({
      start: DateTime.now().minus({ months: 6 }).toISODate() ?? "",
      end: DateTime.now().plus({ years: 1 }).toISODate() ?? ""
    }),
    []
  );

  const [visibleCalendarRange, setVisibleCalendarRange] = useState<VisibleCalendarRange>(null);
  const pageLoadingEventRef = useRef<MbscPageLoadingEvent | null>(null);
  const isRenderingRef = useRef(true);

  const setVisibleRangeFromPageEvent = useCallback((event: MbscPageLoadingEvent) => {
    const start = DateTime.fromJSDate(event.firstDay).toISODate() ?? "";
    const end = DateTime.fromJSDate(event.lastDay).toISODate() ?? "";
    setVisibleCalendarRange({ start, end });
  }, []);

  const handlePageLoading = useCallback(
    (event: MbscPageLoadingEvent) => {
      if (!isEditable) return;
      if (isRenderingRef.current) {
        pageLoadingEventRef.current = event;
      } else {
        setVisibleRangeFromPageEvent(event);
      }
    },
    [isEditable, setVisibleRangeFromPageEvent]
  );

  isRenderingRef.current = true;
  useEffect(() => {
    isRenderingRef.current = false;
    if (pageLoadingEventRef.current) {
      setVisibleRangeFromPageEvent(pageLoadingEventRef.current);
      pageLoadingEventRef.current = null;
    }
  });

  const { calendarRangeParams, singleVehicleEventsParams } = useFleetCalendarParams({
    isEditable,
    showAllEvents,
    selectedRowId: selectedRow?.id,
    stableAllFleetData,
    wideRangeForAdmin,
    bookingRange,
    visibleCalendarRange
  });

  const {
    data: blockingEventsForVehicle,
    isLoading: isLoadingBlockingForVehicle,
    refetch: refetchBlockingForVehicle
  } = useRemoteFetchBlockingEvents(singleVehicleEventsParams);
  const {
    data: blockingEventsCalendar,
    isLoading: isLoadingBlockingCalendar,
    refetch: refetchBlockingCalendar
  } = useRemoteFetchBlockingEventsCalendar(calendarRangeParams);

  const {
    data: bookingEventsForVehicle,
    isLoading: isLoadingBookingForVehicle,
    refetch: refetchBookingForVehicle
  } = useRemoteFetchVehicleBookingEvents(singleVehicleEventsParams);
  const {
    data: bookingEventsCalendar,
    isLoading: isLoadingBookingCalendar,
    refetch: refetchBookingCalendar
  } = useRemoteFetchBookingEventsCalendar(calendarRangeParams);

  const isLoadingEvents = showAllEvents
    ? isLoadingBlockingCalendar || isLoadingBookingCalendar
    : isLoadingBlockingForVehicle || isLoadingBookingForVehicle;

  const blockingEvents = useMemo(() => {
    if (isLoadingEvents) return [];
    const raw = showAllEvents ? blockingEventsCalendar ?? [] : blockingEventsForVehicle ?? [];
    return enrichBlockingEventsWithCreatedByUser(raw, remoteUsers);
  }, [
    isLoadingEvents,
    showAllEvents,
    blockingEventsCalendar,
    blockingEventsForVehicle,
    remoteUsers
  ]);

  const bookingEvents = useMemo(() => {
    if (isLoadingEvents) return [];
    if (showAllEvents) return bookingEventsCalendar ?? [];

    return bookingEventsForVehicle ?? [];
  }, [isLoadingEvents, showAllEvents, bookingEventsCalendar, bookingEventsForVehicle]);

  const { mutateAsync: createBlockingEvent } = useRemoteCreateBlockingEvent();
  const { mutateAsync: updateBlockingEvent } = useRemoteUpdateBlockingEvent();
  const { mutateAsyncSafe: deleteBlockingEvent } = useRemoteDeleteBlockingEvent();
  const { mutateAsync: cancelBooking } = useRemoteCancelFleetBooking();
  const { mutateAsync: checkOutBooking, isLoading: isCheckOutSubmitting } =
    useRemoteCheckOutFleetBooking();
  const { mutateAsync: checkInBooking, isLoading: isCheckInSubmitting } =
    useRemoteCheckInFleetBooking();
  const { mutateAsync: bookOnBehalf } = useRemoteBookOnBehalfFleetBooking();
  const { mutateAsync: updateFleetBookingTimes } = useRemoteUpdateFleetBookingTimes();

  const [isTooltipOpen, setIsTooltipOpen] = useState<boolean>(false);
  const [tooltipContents, setTooltipContents] = useState<FleetEventTooltipContentsUnion>(null);
  const [tooltipAnchor, setTooltipAnchor] = useState<HTMLElement | undefined>(undefined);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [checkInDialogState, setCheckInDialogState] = useState<{
    bookingId: number;
    initialOdometer: number | null;
    mode: "handover" | "return";
  } | null>(null);

  const handleClose = () => {
    setFleetEvents([]);
    setVisibleCalendarRange(null);
    setCheckInDialogState(null);
    onClose();
  };

  const handleCheckOut = (bookingId: number, initialOdometer: number | null = null) => {
    setCheckInDialogState({ bookingId, initialOdometer, mode: "return" });
  };

  const handleCheckIn = (bookingId: number, initialOdometer: number | null = null) => {
    setCheckInDialogState({ bookingId, initialOdometer, mode: "handover" });
  };

  const handleCheckInSubmit = async (payload: {
    odometer?: number | null;
    conditionNotes?: string | null;
    photos?: File[];
  }) => {
    if (checkInDialogState == null) return;
    if (checkInDialogState.mode === "return") {
      await checkOutBooking({ bookingId: checkInDialogState.bookingId, ...payload });
    } else {
      await checkInBooking({ bookingId: checkInDialogState.bookingId, ...payload });
    }
    refetchOnSettled();
  };

  const handleCancel = (bookingId: number) => {
    cancelBooking(bookingId)
      .then(() => refetchOnSettled())
      .catch(error => {
        /** istanbul ignore next */
        handleFleetRequestError(error, enqueueSnackbar, t);
      });
  };

  const getCalendarEventContent = useCallback(
    (eventData: MbscCalendarEventData) => {
      const startValue = eventData.original?.start as Date | string;
      const endValue = eventData.original?.end as Date | string;

      const startDate =
        startValue instanceof Date ? DateTime.fromJSDate(startValue) : DateTime.fromISO(startValue);

      const endDate =
        endValue instanceof Date ? DateTime.fromJSDate(endValue) : DateTime.fromISO(endValue);

      return (
        <Box
          sx={{
            fontSize: "10px",
            lineHeight: "1.2",
            padding: "2px",
            gap: "2px",
            display: "flex"
          }}
        >
          <div style={{ fontWeight: "bold" }}>{eventData.title}</div>
          <div>{startDate.toFormat("HH:mm")}</div>
          <div>{endDate.toFormat("HH:mm")}</div>
        </Box>
      );
    },
    [] // nothing dynamic here
  );

  const { fleetEvents, setFleetEvents } = useFleetEventsState({
    showAllEvents,
    stableAllFleetData,
    selectedRow,
    bookingEvents,
    blockingEvents,
    timezone,
    isEditable,
    bookingDateRange,
    isLoadingEvents,
    t
  });

  const refetchOnSettled = useCallback(async () => {
    onFleetDataRefetch?.();
    if (showAllEvents) {
      await Promise.all([refetchBlockingCalendar(), refetchBookingCalendar()]);
    } else {
      await Promise.all([refetchBlockingForVehicle(), refetchBookingForVehicle()]);
    }
  }, [
    showAllEvents,
    onFleetDataRefetch,
    refetchBlockingForVehicle,
    refetchBlockingCalendar,
    refetchBookingForVehicle,
    refetchBookingCalendar
  ]);

  const withFleetErrorHandling = async <T,>(action: () => Promise<T>): Promise<T> => {
    try {
      const result = await action();
      refetchOnSettled();
      return result;
    } catch (error) {
      /** istanbul ignore next */
      handleFleetRequestError(error, enqueueSnackbar, t);
      throw error;
    }
  };

  const handleCreateBooking = (payload: BookOnBehalfFleetPayload) =>
    withFleetErrorHandling(() => bookOnBehalf(payload));

  const handleCreateBlock = (
    vehicleId: number,
    payload: { start: string; end: string; reason?: string | null; createdBy?: string }
  ) => withFleetErrorHandling(() => createBlockingEvent({ vehicleId, ...payload }));

  const handleUpdateBlock = (
    blockingEventId: number,
    payload: {
      start?: string;
      end?: string;
      reason?: string | null;
      vehicleId?: number;
    }
  ) =>
    withFleetErrorHandling(() =>
      updateBlockingEvent({
        blockingEventId,
        start: payload.start,
        end: payload.end,
        reason: payload.reason,
        vehicleId: payload.vehicleId
      })
    );

  const handleUpdateBooking = (
    bookingId: number,
    payload: {
      start: string;
      end: string;
      userId?: string;
      locationInventoryId?: number;
      reason?: string | null;
      vehicleId?: number;
    }
  ) =>
    withFleetErrorHandling(() =>
      updateFleetBookingTimes({
        bookingId,
        newStartTime: payload.start,
        newEndTime: payload.end,
        bookingType: BookingType.VEHICLE,
        colleagueId: payload.userId,
        locationInventoryId: payload.locationInventoryId,
        tripReason: payload.reason ?? undefined,
        bookingInventoryId: payload.vehicleId
      })
    );

  const handleDeleteBlocks = (blockingEventIds: number[]) =>
    withFleetErrorHandling(() => Promise.all(blockingEventIds.map(id => deleteBlockingEvent(id))));

  const openTooltip = useCallback((args: MbscEventClickEvent) => {
    const event: MbscCalendarEvent = args.event;

    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }

    setTooltipContents(event.originalData ?? null);
    setTooltipAnchor(args.domEvent.target);
    setIsTooltipOpen(true);
  }, []);

  const handleEventClick = useCallback(
    (args: MbscEventClickEvent) => openTooltip(args),
    [openTooltip]
  );

  /** istanbul ignore next */
  const handleMouseEnter = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  /** istanbul ignore next */
  const handleMouseLeave = useCallback(() => {
    timer.current = setTimeout(() => setIsTooltipOpen(false), 200);
  }, []);

  /** istanbul ignore next */
  const handleTooltipClose = useCallback(() => {
    setIsTooltipOpen(false);
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const handleEventHoverIn = useCallback(
    (args: MbscEventClickEvent) => openTooltip(args),
    [openTooltip]
  );

  /** istanbul ignore next */
  const handleEventHoverOut = useCallback(() => {
    if (!timer.current) timer.current = setTimeout(() => setIsTooltipOpen(false), 200);
  }, []);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth={"xl"}>
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {showAllEvents
            ? t("All Fleet Events")
            : t("Events for {{vehicleName}}", { vehicleName: selectedRow?.name ?? "" })}

          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid2
          container
          data-testid="fleet-booking-detail-box"
          sx={{ display: "flex", minHeight: "50vh" }}
        >
          <Grid2 size={{ xs: 12, sm: 5 }} sx={{ pr: 2 }}>
            <Eventcalendar
              key={showAllEvents ? "all-fleet-events" : selectedRow?.id}
              height={"60vh"}
              locale={locale[i18n.language]}
              data={fleetEvents}
              view={calendarMonthView}
              colors={isEditable ? calendarColors : undefined}
              renderEventContent={getCalendarEventContent}
              onEventClick={handleEventClick}
              onEventHoverIn={handleEventHoverIn}
              onEventHoverOut={handleEventHoverOut}
              onPageLoading={handlePageLoading}
            />

            <FleetEventTooltipContents
              contents={tooltipContents}
              tooltipAnchor={tooltipAnchor}
              isTooltipOpen={isTooltipOpen}
              fleetData={stableAllFleetData}
              allUsers={remoteUsers}
              handleTooltipClose={handleTooltipClose}
              handleMouseEnter={handleMouseEnter}
              handleMouseLeave={handleMouseLeave}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 7 }}>
            <FleetManagementEventDetailTable
              isEditable={isEditable}
              selectedRow={showAllEvents ? null : selectedRow}
              users={remoteUsers}
              locationOptions={locationOptions}
              allFleetData={showAllEvents ? allFleetData : undefined}
              blockingEvents={blockingEvents}
              bookingEvents={bookingEvents}
              vehicleIdForNewBlock={showAllEvents ? null : selectedRow?.id ?? null}
              vehicleIdForNewBooking={showAllEvents ? null : selectedRow?.id ?? null}
              handleCheckIn={handleCheckIn}
              handleCheckOut={handleCheckOut}
              handleCancel={handleCancel}
              onBookOnBehalf={handleCreateBooking}
              onCreateBlock={handleCreateBlock}
              onUpdateBlock={handleUpdateBlock}
              onUpdateBooking={handleUpdateBooking}
              onDeleteBlocks={handleDeleteBlocks}
            />
          </Grid2>
        </Grid2>
      </DialogContent>

      <CheckInDialog
        open={checkInDialogState != null}
        bookingId={checkInDialogState?.bookingId ?? null}
        initialOdometer={checkInDialogState?.initialOdometer ?? null}
        mode={checkInDialogState?.mode ?? "handover"}
        onClose={() => setCheckInDialogState(null)}
        onSubmit={handleCheckInSubmit}
        isSubmitting={
          checkInDialogState?.mode === "return" ? isCheckOutSubmitting : isCheckInSubmitting
        }
      />
    </Dialog>
  );
}
